import { InputTextField, InputTextFieldProps } from '@almbrand/inputtextfield';
import { ThemeUnion } from '@almbrand/systemicons/dist/cjs/types/icon';
import { ThemeNames } from 'constantsValues';
import { PageContext } from 'context/PageContext';
import { useContext, useEffect, useState } from 'react';
import { FieldValues, UseFormRegister, UseFormResetField, UseFormSetValue } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import { mapBBRDataToInputValue } from '../../../services/BBRMappingService';
import styles from './InputTextFieldComponent.module.scss';

const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
const numberRegex = /\D/g;
export interface InputTextFieldComponentProps extends InputTextFieldProps<ThemeUnion> {
	register: UseFormRegister<FieldValues>;
	resetField?: UseFormResetField<FieldValues>;
	contentType: string;
	stepContentId: number;
	isValid: boolean;
	hasError?: boolean;
	stepTitle?: string;
	setValue?: UseFormSetValue<FieldValues>;
}

export const InputTextFieldComponent: React.FC<InputTextFieldComponentProps> = ({
	register,
	stepContentId,
	contentType,
	resetField,
	hasError,
	setValue,
	...props
}) => {
	const pageContext = useContext(PageContext);
	const { theme, metaArea } = pageContext ?? {};
	const themeName = ThemeNames[theme];

	const [hasValidateError, setHasValidateError] = useState(hasError);
	const updatedComponentProps = {
		...props,
		themeName: themeName,
	};

	const { onBlur, onChange, name } = register(stepContentId.toString(), { required: props.required });

	const invalidateField = () => {
		resetField(name);
		updatedComponentProps.isValid = false;
		setHasValidateError(true);
	};

	const isValid = (value) => {
		let length;
		switch (updatedComponentProps.type) {
			case 'number':
				if (value >= (updatedComponentProps.min as number) && value <= (updatedComponentProps.max as number)) {
					return true;
				}
				return false;
			case 'tel':
				length = value?.replaceAll(numberRegex, '')?.length;
				return length >= 8 && length <= 10;

			case 'text':
				if (
					value.length >= (updatedComponentProps.min as number) &&
					value.length <= (updatedComponentProps.max as number)
				) {
					return true;
				}
				return false;
			case 'email':
				return value?.match(emailRegex) !== null;

			default:
				return value?.length > 0;
		}
	};

	const handleOnBlur = (evt) => {
		if (!isValid(evt.target.value)) {
			invalidateField();
			return;
		}
		setHasValidateError(false);
		updatedComponentProps.isValid = true;
		onBlur({
			target: {
				name,
				value: {
					id: name,
					value: evt.target?.value,
					type: contentType,
					stepTitle: props.stepTitle,
					inputLabel: props.label,
				},
			},
		});
	};

	const handleOnChange = (evt) => {
		const valid = isValid(evt.target.value);

		if (valid) {
			setHasValidateError(false);
			updatedComponentProps.isValid = true;
			onChange({
				target: {
					name,
					value: {
						id: name,
						value: evt.target?.value,
						type: contentType,
						stepTitle: props.stepTitle,
						inputLabel: props.label,
					},
				},
			});
		} else if (updatedComponentProps.isValid) {
			invalidateField();
		}
	};

	useEffect(() => {
		if (!hasValidateError) {
			setHasValidateError(hasError);
		}
	}, [hasError]);

	const data = useAppSelector((state) => state.calculatorForm[name]?.data);

	const staticFieldItems = pageContext?.metaArea?.items?.[0]?.content?.staticFields?.items ?? [];
	const rawDefaultValue = staticFieldItems.find((item) => item?.content?.uniqueIdentifier?.uid === name)?.content
		?.defaultValue;
	const matchedDefaultValue = rawDefaultValue?.toString().replace(numberRegex, '');
	const resolvedValue = data?.value ?? matchedDefaultValue;

	useEffect(() => {
		if (setValue && resolvedValue) {
			setValue(name, {
				id: name,
				value: resolvedValue,
				type: contentType,
				stepTitle: props.stepTitle,
				inputLabel: props.label,
			});
		}
	}, [contentType, name, props.label, props.stepTitle, resolvedValue, setValue]);

	return (
		<div className={styles.InputTextFieldComponent}>
			<InputTextField
				{...updatedComponentProps}
				onBlur={handleOnBlur}
				onChange={handleOnChange}
				hasError={hasValidateError}
				value={rawDefaultValue?.toString() || mapBBRDataToInputValue(data, metaArea) || ''}
			/>
		</div>
	);
};
